import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Badge,
  Button,
  Chip,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  AnnouncementOutlined,
  CallOutlined,
  DescriptionOutlined,
  EventOutlined,
  Facebook,
  HeadsetMicOutlined,
  ImageOutlined,
  Instagram,
  InsertEmoticonOutlined,
  LocalOfferOutlined,
  PermContactCalendarOutlined,
  RoomOutlined,
  TextsmsOutlined,
  ViewListOutlined,
  VideocamOutlined,
  WhatsApp,
} from "@material-ui/icons";
import Board from "react-trello";
import { toast } from "react-toastify";
import { format, isSameDay, parseISO } from "date-fns";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { Can } from "../../components/Can";
import AppliedFiltersBar from "../../components/AppliedFiltersBar";
import FilterButton from "../../components/FilterButton";
import FilterPopover from "../../components/FilterPopover";
import MetricsStrip from "../../components/MetricsStrip";
import toastError from "../../errors/toastError";
import {
  extractMovedTicket,
  getUnassignedKanbanTickets,
  replaceTicketInList,
  UNASSIGNED_KANBAN_LANE_ID,
} from "./kanbanStageUtils";

const UNASSIGNED_KANBAN_LANE_TITLE = "Em aberto";
const UNASSIGNED_KANBAN_LANE_COLOR = "#64748B";

const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== "string") {
    return `rgba(255, 0, 82, ${alpha})`;
  }

  const sanitized = hex.replace("#", "");
  const normalized =
    sanitized.length === 3
      ? sanitized
          .split("")
          .map((char) => char + char)
          .join("")
      : sanitized;

  const value = parseInt(normalized, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const getReadableColor = (hex, fallback = "#ffffff") => {
  if (!hex || typeof hex !== "string") {
    return fallback;
  }

  const sanitized = hex.replace("#", "");
  const normalized =
    sanitized.length === 3
      ? sanitized
          .split("")
          .map((char) => char + char)
          .join("")
      : sanitized;

  const value = parseInt(normalized, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  const yiq = (red * 299 + green * 587 + blue * 114) / 1000;

  return yiq >= 148 ? "#0a1b22" : "#ffffff";
};

const KANBAN_LANE_WIDTH = 320;
const KANBAN_CARD_SIDE_GUTTER = 12;
const KANBAN_CARD_MAX_WIDTH = KANBAN_LANE_WIDTH - KANBAN_CARD_SIDE_GUTTER * 2;
const KANBAN_EMPTY_DROP_HEIGHT = 190;

const getMessageTypeLabel = (ticket, compactMessage) => {
  const rawType =
    ticket?.lastMessageMediaType ||
    ticket?.lastMessageType ||
    ticket?.mediaType ||
    ticket?.lastMessage?.mediaType ||
    "";
  const normalizedType = String(rawType).toLowerCase();
  const rawMessage = String(ticket?.lastMessage || "");

  if (normalizedType === "locationmessage") {
    return i18n.t("mainDrawer.appBar.message.location") || "Localização";
  }

  if (normalizedType === "contactmessage") {
    return i18n.t("mainDrawer.appBar.message.contact") || "Contato";
  }

  if (normalizedType === "admetapreview") {
    return "Clique de Anúncio";
  }

  if (normalizedType === "template") {
    return "Template";
  }

  if (normalizedType === "reactionmessage") {
    return "Reação";
  }

  if (normalizedType === "call_log") {
    return "Ligação";
  }

  if (normalizedType.includes("event")) {
    return "Evento";
  }


  if (normalizedType.includes("audio")) {
    return "Áudio";
  }

  if (normalizedType.includes("video")) {
    return "Vídeo";
  }

  if (normalizedType.includes("image")) {
    return "Imagem";
  }

  if (normalizedType.includes("application") || normalizedType.includes("document")) {
    return "Documento";
  }

  if (normalizedType.includes("sticker")) {
    return "Figurinha";
  }

  if (compactMessage.includes("fb.me")) {
    return "Clique de Anúncio";
  }

  if (/data:image\/[a-zA-Z]+;base64,/i.test(compactMessage)) {
    return i18n.t("mainDrawer.appBar.message.location") || "Localização";
  }

  if (rawMessage.includes("BEGIN:VCARD") || rawMessage.includes("END:VCARD")) {
    return i18n.t("mainDrawer.appBar.message.contact") || "Contato";
  }

  if (
    rawMessage.includes("BEGIN:VCALENDAR") ||
    rawMessage.includes("BEGIN:VEVENT") ||
    /\.ics$/i.test(compactMessage)
  ) {
    return "Evento";
  }


  if (/^-?\d+(?:\.\d+)?\|-?\d+(?:\.\d+)?(?:\|.*)?$/.test(compactMessage)) {
    return i18n.t("mainDrawer.appBar.message.location") || "Localização";
  }

  if (/\.(pdf|doc|docx|xls|xlsx|csv|txt|zip|rar)$/i.test(compactMessage)) {
    return "Documento";
  }

  if (/\.(mp3|ogg|wav|opus|m4a)$/i.test(compactMessage)) {
    return "Áudio";
  }

  if (/\.(mp4|avi|mov|webm|mkv)$/i.test(compactMessage)) {
    return "Vídeo";
  }

  if (/\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(compactMessage)) {
    return "Imagem";
  }

  return null;
};

const getLastMessagePreview = (ticket) => {
  const message = ticket?.lastMessage;

  if (!message) {
    return "Sem mensagem recente.";
  }

  const normalizedLines = String(message)
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const lastLine = normalizedLines[normalizedLines.length - 1] || String(message).trim();
  const compactMessage = lastLine.replace(/\s+/g, " ").trim();

  if (!compactMessage) {
    return "Sem mensagem recente.";
  }

  const mediaLabel = getMessageTypeLabel(ticket, compactMessage);

  if (mediaLabel) {
    return mediaLabel;
  }

  return compactMessage.length > 140
    ? `${compactMessage.slice(0, 137)}...`
    : compactMessage;
};

const getMessageTypeKey = (ticket, compactMessage) => {
  const rawType =
    ticket?.lastMessageMediaType ||
    ticket?.lastMessageType ||
    ticket?.mediaType ||
    ticket?.lastMessage?.mediaType ||
    "";
  const normalizedType = String(rawType).toLowerCase();
  const rawMessage = String(ticket?.lastMessage || "");

  if (normalizedType === "locationmessage") {
    return "location";
  }

  if (normalizedType === "contactmessage") {
    return "contact";
  }

  if (normalizedType === "admetapreview") {
    return "ad";
  }

  if (normalizedType === "template") {
    return "template";
  }

  if (normalizedType === "reactionmessage") {
    return "reaction";
  }

  if (normalizedType === "call_log") {
    return "call";
  }

  if (normalizedType.includes("event")) {
    return "event";
  }

  if (normalizedType.includes("audio")) {
    return "audio";
  }

  if (normalizedType.includes("video")) {
    return "video";
  }

  if (normalizedType.includes("image")) {
    return "image";
  }

  if (normalizedType.includes("application") || normalizedType.includes("document")) {
    return "document";
  }

  if (normalizedType.includes("sticker")) {
    return "sticker";
  }

  if (compactMessage.includes("fb.me")) {
    return "ad";
  }

  if (/data:image\/[a-zA-Z]+;base64,/i.test(compactMessage)) {
    return "location";
  }

  if (rawMessage.includes("BEGIN:VCARD") || rawMessage.includes("END:VCARD")) {
    return "contact";
  }

  if (
    rawMessage.includes("BEGIN:VCALENDAR") ||
    rawMessage.includes("BEGIN:VEVENT") ||
    /\.ics$/i.test(compactMessage)
  ) {
    return "event";
  }

  if (/^-?\d+(?:\.\d+)?\|-?\d+(?:\.\d+)?(?:\|.*)?$/.test(compactMessage)) {
    return "location";
  }

  if (/\.(pdf|doc|docx|xls|xlsx|csv|txt|zip|rar)$/i.test(compactMessage)) {
    return "document";
  }

  if (/\.(mp3|ogg|wav|opus|m4a)$/i.test(compactMessage)) {
    return "audio";
  }

  if (/\.(mp4|avi|mov|webm|mkv)$/i.test(compactMessage)) {
    return "video";
  }

  if (/\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(compactMessage)) {
    return "image";
  }

  return "text";
};

const getLastMessagePreviewIcon = (ticket, messagePreview) => {
  const normalizedPreview = String(messagePreview || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalizedPreview.includes("audio")) {
    return "audio";
  }

  if (normalizedPreview.includes("video")) {
    return "video";
  }

  if (normalizedPreview.includes("imagem")) {
    return "image";
  }

  if (normalizedPreview.includes("documento")) {
    return "document";
  }

  if (normalizedPreview.includes("contato")) {
    return "contact";
  }

  if (normalizedPreview.includes("localizacao")) {
    return "location";
  }

  if (normalizedPreview.includes("figurinha")) {
    return "sticker";
  }

  if (normalizedPreview.includes("ligacao")) {
    return "call";
  }

  if (normalizedPreview.includes("reacao")) {
    return "reaction";
  }

  if (normalizedPreview.includes("template")) {
    return "template";
  }

  if (normalizedPreview.includes("evento")) {
    return "event";
  }

  if (normalizedPreview.includes("clique de anuncio")) {
    return "ad";
  }

  const message = ticket?.lastMessage;

  if (!message) {
    return "text";
  }

  const normalizedLines = String(message)
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const lastLine = normalizedLines[normalizedLines.length - 1] || String(message).trim();
  const compactMessage = lastLine.replace(/\s+/g, " ").trim();

  if (!compactMessage) {
    return "text";
  }

  return getMessageTypeKey(ticket, compactMessage);
};

const useStyles = makeStyles((theme) => ({
  "@global": {
    ".react-trello-board": {
      background: "transparent !important",
      padding: "0 !important",
      gap: "16px",
    },
    ".react-trello-lane": {
      borderRadius: "20px !important",
      width: `${KANBAN_LANE_WIDTH}px !important`,
      minWidth: `${KANBAN_LANE_WIDTH}px !important`,
      maxWidth: `${KANBAN_LANE_WIDTH}px !important`,
      flex: `0 0 ${KANBAN_LANE_WIDTH}px !important`,
      padding: "0 !important",
      boxSizing: "border-box !important",
      marginRight: "16px !important",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 18px 38px rgba(0, 0, 0, 0.22) !important"
          : "0 18px 38px rgba(15, 23, 42, 0.08) !important",
      overflow: "visible !important",
    },
    ".react-trello-lane .smooth-dnd-container": {
      width: "100% !important",
      minWidth: "0 !important",
      maxWidth: "100% !important",
      padding: "0 6px 12px !important",
      boxSizing: "border-box !important",
      overflowX: "hidden !important",
      overflowY: "visible !important",
    },
    ".react-trello-lane .smooth-dnd-draggable-wrapper": {
      display: "block !important",
      width: "100% !important",
      minWidth: "0 !important",
      maxWidth: "100% !important",
      overflow: "visible !important",
    },
    ".react-trello-lane header": {
      padding: "16px 18px 10px !important",
      background: "transparent !important",
      borderBottom: `1px solid ${theme.palette.divider} !important`,
    },
    ".react-trello-lane footer": {
      background: "transparent !important",
      borderTop: `1px solid ${theme.palette.divider} !important`,
    },
    ".react-trello-card": {
      borderRadius: "18px !important",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 14px 30px rgba(0, 0, 0, 0.2) !important"
          : "0 12px 24px rgba(15, 23, 42, 0.07) !important",
      display: "block !important",
      margin: "0 auto 12px !important",
      border: `1px solid ${theme.palette.divider} !important`,
      background: `${theme.palette.background.paper} !important`,
      color: `${theme.palette.text.primary} !important`,
      cursor: "grab",
      width: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      minWidth: "0 !important",
      maxWidth: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      boxSizing: "border-box !important",
    },
    ".react-trello-card:active": {
      cursor: "grabbing",
    },
    ".smooth-dnd-ghost": {
      zIndex: "2000 !important",
      width: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      minWidth: "0 !important",
      maxWidth: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      boxSizing: "border-box !important",
      overflow: "visible !important",
      opacity: "1 !important",
    },
    ".smooth-dnd-ghost > *": {
      width: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      minWidth: "0 !important",
      maxWidth: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      margin: "0 !important",
      boxSizing: "border-box !important",
    },
    ".smooth-dnd-ghost .smooth-dnd-draggable-wrapper": {
      overflow: "visible !important",
    },
    ".smooth-dnd-ghost .react-trello-card": {
      width: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      minWidth: "0 !important",
      maxWidth: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      margin: "0 !important",
      opacity: "1 !important",
      background: `${theme.palette.background.paper} !important`,
      border: `1px solid ${theme.palette.divider} !important`,
      boxShadow:
        theme.palette.type === "dark"
          ? "0 24px 60px rgba(0, 0, 0, 0.34) !important"
          : "0 24px 60px rgba(15, 23, 42, 0.18) !important",
    },
    ".fluyra-kanban-dragging": {
      width: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      minWidth: "0 !important",
      maxWidth: `${KANBAN_CARD_MAX_WIDTH}px !important`,
      transform: "none !important",
      opacity: "1 !important",
    },
  },
  root: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  pageShell: {
    width: "100%",
    maxWidth: "none",
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  headerRow: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
  },
  heroTitle: {
    fontWeight: 600,
    fontSize: 26,
    lineHeight: 1.1,
    color: theme.palette.text.primary,
    letterSpacing: "-0.01em",
  },
  heroDescription: {
    maxWidth: 720,
    color: theme.palette.text.secondary,
  },
  filterInput: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius,
    },
  },
  primaryAction: {
    borderRadius: 12,
    height: 40,
    padding: theme.spacing(0, 2),
    fontWeight: 700,
  },
  secondaryAction: {
    borderRadius: 12,
    height: 40,
    padding: theme.spacing(0, 2),
    fontWeight: 700,
  },
  boardSection: {
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: "none",
    overflow: "hidden",
  },
  boardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  boardTitleWrap: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  boardTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  boardSubtitle: {
    color: theme.palette.text.secondary,
  },
  boardMeta: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  boardChip: {
    borderRadius: 999,
    fontWeight: 700,
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
    border: `1px solid ${theme.palette.divider}`,
  },
  kanbanContainer: {
    width: "100%",
    overflowX: "auto",
    padding: theme.spacing(1.5),
    ...theme.scrollbarStyles,
  },
  cardRoot: {
    padding: 16,
    outline: "none",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  cardShell: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
    minWidth: 0,
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    minWidth: 0,
  },
  cardIdentity: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: "1 1 auto",
    minWidth: 0,
  },
  contactBlock: {
    minWidth: 0,
    flex: "1 1 auto",
  },
  channelBadge: {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: 12,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
    border: `1px solid ${theme.palette.divider}`,
  },
  contactName: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  ticketMeta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  timePill: {
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: "nowrap",
    flexShrink: 0,
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
    color: theme.palette.text.secondary,
  },
  timePillUnread: {
    background: theme.fluyra.tokens.accentSoft,
    color: theme.palette.primary.main,
  },
  messagePreview: {
    padding: theme.spacing(1.25),
    borderRadius: 14,
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
    color: theme.palette.text.secondary,
    minHeight: 64,
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },
  messagePreviewIcon: {
    width: 18,
    height: 18,
    minWidth: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    opacity: 0.88,
    marginTop: 1,
    flexShrink: 0,
  },
  messagePreviewText: {
    minWidth: 0,
    maxWidth: "100%",
    lineHeight: 1.45,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    minWidth: 0,
  },
  ownerTag: {
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.07)",
    color: theme.palette.text.primary,
    borderRadius: 999,
    fontSize: "0.7rem",
    fontWeight: 700,
    padding: "2px 8px",
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  emptyOwner: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    flex: "1 1 0",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 12,
    fontWeight: 700,
    flexShrink: 0,
    padding: theme.spacing(0.75, 1.5),
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  scrollableLane: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    alignSelf: "stretch",
    width: "100%",
    maxHeight: "90vh",
    minHeight: 180,
    marginTop: 10,
    paddingBottom: 12,
    transition: theme.transitions.create(["background-color", "box-shadow", "transform"], {
      duration: theme.transitions.duration.shorter,
    }),
    "& .smooth-dnd-container": {
      flex: 1,
      position: "relative",
      minHeight: 110,
      transition: theme.transitions.create(["background-color", "box-shadow"], {
        duration: theme.transitions.duration.shorter,
      }),
    },
    "& .smooth-dnd-container:empty": {
      minHeight: KANBAN_EMPTY_DROP_HEIGHT,
    },
  },
  scrollableLaneActive: {
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(255,0,82,0.04)",
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    transform: "translateY(-1px)",
    "& .smooth-dnd-container": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.02)"
          : "rgba(255,0,82,0.03)",
      boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
      borderRadius: 18,
    },
    "& $dropHint": {
      opacity: 1,
      transform: "scale(1)",
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(255,0,82,0.06)",
    },
  },
  dropHint: {
    position: "absolute",
    left: 6,
    right: 6,
    bottom: 12,
    height: KANBAN_EMPTY_DROP_HEIGHT - 24,
    borderRadius: 16,
    border: `1px dashed ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    background:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.03)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(2),
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.01em",
    pointerEvents: "none",
    opacity: 0,
    transform: "scale(0.98)",
    transition: theme.transitions.create(
      ["opacity", "transform", "border-color", "background-color", "color"],
      {
        duration: theme.transitions.duration.shorter,
      }
    ),
  },
}));

const KanbanCard = ({ id, title, label, metadata, className, style, onClick }) => {
  const classes = useStyles();
  const theme = useTheme();
  const ticket = metadata?.ticket;

  const renderChannelIcon = () => {
    switch (ticket?.channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", fontSize: 18 }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", fontSize: 18 }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", fontSize: 18 }} />;
      default:
        return <WhatsApp style={{ color: theme.palette.primary.main, fontSize: 18 }} />;
    }
  };

  const renderPreviewIcon = () => {
    switch (metadata?.messagePreviewIcon) {
      case "location":
        return <RoomOutlined style={{ fontSize: 16 }} />;
      case "contact":
        return <PermContactCalendarOutlined style={{ fontSize: 16 }} />;
      case "ad":
        return <AnnouncementOutlined style={{ fontSize: 16 }} />;
      case "template":
        return <ViewListOutlined style={{ fontSize: 16 }} />;
      case "reaction":
        return <InsertEmoticonOutlined style={{ fontSize: 16 }} />;
      case "call":
        return <CallOutlined style={{ fontSize: 16 }} />;
      case "event":
        return <EventOutlined style={{ fontSize: 16 }} />;
      case "audio":
        return <HeadsetMicOutlined style={{ fontSize: 16 }} />;
      case "video":
        return <VideocamOutlined style={{ fontSize: 16 }} />;
      case "image":
        return <ImageOutlined style={{ fontSize: 16 }} />;
      case "document":
        return <DescriptionOutlined style={{ fontSize: 16 }} />;
      case "sticker":
        return <LocalOfferOutlined style={{ fontSize: 16 }} />;
      case "text":
      default:
        return <TextsmsOutlined style={{ fontSize: 16 }} />;
    }
  };

  return (
    <div
      data-id={id}
      className={className}
      style={style}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          onClick?.(event);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className={classes.cardRoot}>
        <div className={classes.cardShell}>
          <div className={classes.cardTop}>
            <div className={classes.cardIdentity}>
              <div className={classes.channelBadge}>
                <Tooltip title={ticket?.whatsapp?.name || ticket?.channel || "Canal"}>
                  <span>{renderChannelIcon()}</span>
                </Tooltip>
              </div>

              <div className={classes.contactBlock}>
                <div className={classes.contactName}>{title}</div>
                <div className={classes.ticketMeta}>
                  {label}
                  {ticket?.contact?.number ? ` • ${ticket.contact.number}` : ""}
                </div>
              </div>
            </div>

            <div
              className={`${classes.timePill} ${
                Number(ticket?.unreadMessages) > 0 ? classes.timePillUnread : ""
              }`}
            >
              {metadata?.timeLabel || "--"}
            </div>
          </div>

          <div className={classes.messagePreview}>
            <span className={classes.messagePreviewIcon} aria-hidden="true">
              {renderPreviewIcon()}
            </span>
            <span className={classes.messagePreviewText}>
              {metadata?.messagePreview || "Sem mensagem recente."}
            </span>
          </div>

          <div className={classes.cardFooter}>
            {ticket?.user ? (
              <Badge className={classes.ownerTag}>{ticket.user.name.toUpperCase()}</Badge>
            ) : (
              <span className={classes.emptyOwner}>Sem responsável</span>
            )}

            <Button
              className={classes.cardButton}
              size="small"
              onMouseDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                metadata?.onOpenTicket?.(ticket?.uuid);
              }}
            >
              Ver Ticket
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const KanbanScrollableLane = React.forwardRef(
  ({ children, className, isDraggingOver, ...props }, ref) => {
    const classes = useStyles();
    const laneClassName = [
      classes.scrollableLane,
      isDraggingOver ? classes.scrollableLaneActive : "",
      className || "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div ref={ref} className={laneClassName} {...props}>
        {children}
        <div className={classes.dropHint}>Solte o ticket aqui</div>
      </div>
    );
  }
);

KanbanScrollableLane.displayName = "KanbanScrollableLane";

const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [file, setFile] = useState({ lanes: [] });
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const moveSequenceRef = useRef({});
  const pendingMoveSocketRef = useRef({});

  const queueIds = useMemo(
    () => user.queues.map((queue) => queue.UserQueue.queueId),
    [user.queues]
  );

  const totalTickets = tickets.length;
  const unreadTickets = tickets.filter((ticket) => Number(ticket.unreadMessages) > 0).length;
  const taggedTickets = tickets.filter((ticket) => ticket.tags?.length > 0).length;
  const activeLanes = tags.length + 1;

  const handleCardClick = useCallback(
    (uuid) => {
      if (uuid) {
        history.push(`/tickets/${uuid}`);
      }
    },
    [history]
  );

  const formatTicketTime = useCallback((updatedAt) => {
    if (!updatedAt) {
      return "--";
    }

    return isSameDay(parseISO(updatedAt), new Date())
      ? format(parseISO(updatedAt), "HH:mm")
      : format(parseISO(updatedAt), "dd/MM/yyyy");
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      const { data } = await api.get("/ticket/kanban", {
        params: {
          queueIds: JSON.stringify(queueIds),
          startDate,
          endDate,
        },
      });

      setTickets(data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  }, [endDate, queueIds, startDate]);

  const fetchTags = useCallback(async () => {
    try {
      const response = await api.get("/tag/kanban/");
      setTags(response.data.lista || []);
      fetchTickets();
    } catch (error) {
      console.log(error);
    }
  }, [fetchTickets]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    const companyId = user.companyId;
    const onAppMessage = (data) => {
      const socketTicketId = data.ticket?.id?.toString();

      if (
        data.action === "update" &&
        socketTicketId &&
        pendingMoveSocketRef.current[socketTicketId]
      ) {
        const pendingMove = pendingMoveSocketRef.current[socketTicketId];
        const socketTicketTags = data.ticket?.tags || [];
        const socketReachedTargetLane = pendingMove.tagId === null
          ? !socketTicketTags.some((ticketTag) =>
              tags.some((tag) => Number(tag.id) === Number(ticketTag.id))
            )
          : socketTicketTags.some(
              (tag) => Number(tag.id) === Number(pendingMove.tagId)
            );

        if (socketReachedTargetLane) {
          delete pendingMoveSocketRef.current[socketTicketId];
          setTickets((currentTickets) => replaceTicketInList(currentTickets, data.ticket));
        }

        return;
      }

      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [fetchTickets, socket, tags, user.companyId]);

  useEffect(() => {
    const companyId = user.companyId;
    const onTagsEvent = () => {
      fetchTags();
    };

    socket.on(`company${companyId}-tag`, onTagsEvent);

    return () => {
      socket.off(`company${companyId}-tag`, onTagsEvent);
    };
  }, [fetchTags, socket, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleOpenFilters = (event) => {
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => setFilterAnchorEl(null);

  const handleApplyFilters = () => {
    if (draftStartDate && draftEndDate && draftStartDate > draftEndDate) {
      toast.error("A data inicial deve ser anterior a data final.");
      return;
    }

    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    handleCloseFilters();
  };

  const handleClearDraftFilters = () => {
    setDraftStartDate("");
    setDraftEndDate("");
  };

  const handleRemoveFilter = (key) => {
    if (key === "startDate") setStartDate("");
    if (key === "endDate") setEndDate("");
  };

  const handleClearAppliedFilters = () => {
    setStartDate("");
    setEndDate("");
    setDraftStartDate("");
    setDraftEndDate("");
  };

  const activeFilterCount = [startDate, endDate].filter(Boolean).length;
  const appliedFilters = [
    startDate
      ? { key: "startDate", label: `Desde: ${format(parseISO(startDate), "dd/MM/yyyy")}` }
      : null,
    endDate
      ? { key: "endDate", label: `Ate: ${format(parseISO(endDate), "dd/MM/yyyy")}` }
      : null,
  ].filter(Boolean);

  const buildLane = useCallback(
    (laneId, laneTitle, laneColor, laneTickets) => {
      const tintAlpha = theme.palette.type === "dark" ? 0.18 : 0.12;
      const borderAlpha = theme.palette.type === "dark" ? 0.34 : 0.24;
      const laneBackground = laneColor
        ? hexToRgba(laneColor, tintAlpha)
        : theme.palette.type === "dark"
          ? "rgba(255,255,255,0.03)"
          : "rgba(15,23,42,0.04)";
      const laneBorder = laneColor ? hexToRgba(laneColor, borderAlpha) : theme.palette.divider;

      return {
        id: laneId,
        title: laneTitle,
        label: laneTickets.length.toString(),
        style: {
          backgroundColor: laneBackground,
          border: `1px solid ${laneBorder}`,
        },
        labelStyle: {
          backgroundColor: laneColor || theme.palette.primary.main,
          color: getReadableColor(laneColor || "#ff0052"),
          borderRadius: "999px",
          padding: "4px 10px",
          fontWeight: 700,
          fontSize: "12px",
        },
        cards: laneTickets.map((ticket) => {
          const messagePreview = getLastMessagePreview(ticket);
          const messagePreviewIcon = getLastMessagePreviewIcon(ticket, messagePreview);

          return {
            id: ticket.id.toString(),
            title: ticket.contact?.name || `Ticket #${ticket.id}`,
            label: `Ticket #${ticket.id}`,
            description: messagePreview,
            metadata: {
              ticket,
              timeLabel: formatTicketTime(ticket.updatedAt),
              messagePreview,
              messagePreviewIcon,
              onOpenTicket: handleCardClick,
            },
            draggable: true,
          };
        }),
      };
    },
    [formatTicketTime, handleCardClick, theme.palette.divider, theme.palette.primary.main, theme.palette.type]
  );

  const rebuildBoard = useCallback(() => {
    const unassignedTickets = getUnassignedKanbanTickets(tickets, tags);
    const lanes = [
      buildLane(
        UNASSIGNED_KANBAN_LANE_ID,
        UNASSIGNED_KANBAN_LANE_TITLE,
        UNASSIGNED_KANBAN_LANE_COLOR,
        unassignedTickets
      ),
      ...tags.map((tag) => {
        const laneTickets = tickets.filter((ticket) =>
          (ticket.tags || []).map((ticketTag) => ticketTag.id).includes(tag.id)
        );

        return buildLane(tag.id.toString(), tag.name, tag.color, laneTickets);
      }),
    ];

    setFile({ lanes });
  }, [buildLane, tags, tickets]);

  useEffect(() => {
    rebuildBoard();
  }, [rebuildBoard]);

  const handleCardMove = async (fromLaneId, toLaneId, cardId, index) => {
    if (fromLaneId === toLaneId) {
      return;
    }

    const isUnassignedTarget = toLaneId === UNASSIGNED_KANBAN_LANE_ID;
    const tagId = isUnassignedTarget ? null : Number(toLaneId);

    if (!isUnassignedTarget && (!Number.isInteger(tagId) || tagId <= 0)) {
      toast.error(i18n.t("backendErrors.ERR_INVALID_KANBAN_STAGE"));
      await fetchTickets();
      return;
    }

    const moveSequence = (moveSequenceRef.current[cardId] || 0) + 1;
    moveSequenceRef.current[cardId] = moveSequence;
    pendingMoveSocketRef.current[cardId] = { moveSequence, tagId };

    setFile((currentBoard) => {
      const currentLanes = currentBoard.lanes || [];
      const sourceLane = currentLanes.find((lane) => lane.id === fromLaneId);
      const cardToMove = sourceLane?.cards?.find((card) => card.id === cardId);

      if (!cardToMove || fromLaneId === toLaneId) {
        return currentBoard;
      }

      const nextLanes = currentLanes.map((lane) => {
        if (lane.id === fromLaneId) {
          const cards = lane.cards.filter((card) => card.id !== cardId);
          return { ...lane, cards, label: cards.length.toString() };
        }

        if (lane.id === toLaneId) {
          const cards = [...lane.cards];
          const targetIndex = typeof index === "number" ? index : cards.length;
          cards.splice(targetIndex, 0, cardToMove);
          return { ...lane, cards, label: cards.length.toString() };
        }

        return lane;
      });

      return { lanes: nextLanes };
    });

    try {
      const { data } = isUnassignedTarget
        ? await api.delete(`/ticket-tags/${cardId}`)
        : await api.put(`/ticket-tags/${cardId}/move-stage`, { tagId });

      if (moveSequenceRef.current[cardId] !== moveSequence) {
        return;
      }

      const movedTicket = extractMovedTicket(data);

      if (movedTicket) {
        setTickets((currentTickets) => replaceTicketInList(currentTickets, movedTicket));
      } else {
        await fetchTickets();
      }

      setTimeout(() => {
        if (pendingMoveSocketRef.current[cardId]?.moveSequence === moveSequence) {
          delete pendingMoveSocketRef.current[cardId];
        }
      }, 2500);

      toast.success("Ticket movido com sucesso!");
    } catch (err) {
      console.log(err);
      if (moveSequenceRef.current[cardId] !== moveSequence) {
        return;
      }
      if (pendingMoveSocketRef.current[cardId]?.moveSequence === moveSequence) {
        delete pendingMoveSocketRef.current[cardId];
      }
      toastError(err);
      await fetchTickets();
    }
  };

  const handleAddConnectionClick = () => {
    history.push("/tagsKanban");
  };

  return (
    <div className={classes.root}>
      <div className={classes.pageShell}>
        <div className={classes.headerRow}>
          <div className={classes.heroTitleBlock}>
            <Typography className={classes.eyebrow}>Operacao</Typography>
            <Typography className={classes.heroTitle}>Kanban operacional</Typography>
            <Typography className={classes.heroDescription}>
              Conversas ativas organizadas por etapa de atendimento.
            </Typography>
          </div>

          <div className={classes.headerActions}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.secondaryAction}
              onClick={handleSearchClick}
            >
              Atualizar
            </Button>
            <FilterButton
              count={activeFilterCount}
              open={Boolean(filterAnchorEl)}
              controls="kanban-filters"
              onClick={handleOpenFilters}
            />
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.primaryAction}
                  onClick={handleAddConnectionClick}
                >
                  Colunas
                </Button>
              )}
            />
          </div>
        </div>

        <MetricsStrip
          ariaLabel="Indicadores do Kanban operacional"
          items={[
            { key: "tickets", label: "Tickets no quadro", value: totalTickets },
            { key: "lanes", label: "Lanes ativas", value: activeLanes },
            { key: "tagged", label: "Com tags", value: taggedTickets },
            { key: "unread", label: "Nao lidos", value: unreadTickets },
          ]}
        />

        <FilterPopover
          id="kanban-filters"
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilters}
          onClear={handleClearDraftFilters}
          onApply={handleApplyFilters}
          clearDisabled={!draftStartDate && !draftEndDate}
        >
            <TextField
              label="Data de inicio"
              type="date"
              value={draftStartDate}
              onChange={(event) => setDraftStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              className={classes.filterInput}
            />
            <TextField
              label="Data de fim"
              type="date"
              value={draftEndDate}
              onChange={(event) => setDraftEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              className={classes.filterInput}
            />
        </FilterPopover>

        <Paper className={classes.boardSection} elevation={0}>
          <div className={classes.boardHeader}>
            <div className={classes.boardTitleWrap}>
              <Typography variant="h6" className={classes.boardTitle}>
                Tickets por etapa operacional
              </Typography>
            </div>

            <div className={classes.boardMeta}>
              <Chip label={`${queueIds.length} filas`} className={classes.boardChip} />
              <Chip
                label={`${totalTickets} tickets carregados`}
                className={classes.boardChip}
              />
            </div>
          </div>

          <AppliedFiltersBar
            filters={appliedFilters}
            onRemove={handleRemoveFilter}
            onClearAll={handleClearAppliedFilters}
          />

          <div className={classes.kanbanContainer}>
            <Board
              data={file}
              onCardMoveAcrossLanes={handleCardMove}
              onCardClick={(cardId, metadata) => {
                if (metadata?.ticket?.uuid) {
                  handleCardClick(metadata.ticket.uuid);
                }
              }}
              laneDraggable={false}
              cardDraggable
              cardDragClass="fluyra-kanban-dragging"
              components={{ Card: KanbanCard, ScrollableLane: KanbanScrollableLane }}
              style={{ backgroundColor: "transparent" }}
            />
          </div>
        </Paper>
      </div>
    </div>
  );
};

export default Kanban;
